import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../components/Card';
import { RequestSideCodeModal } from '../components/RequestSideCodeModal';
import { KpiCard } from '../components/KpiCard';
import { Pill } from '../components/Pill';
import { CopyField } from '../components/CopyField';
import { EarningsChart } from '../components/EarningsChart';
import { CountUp } from '../components/CountUp';
import { Icon } from '../components/Icons';
import { btnGhost, btnIcon, tableStyle, tdStyle, thStyle } from '../components/buttons';
import { creatorApi } from '../api/creator';
import { useAuthStore } from '../auth/store';
import { fmtDate, fmtNum, fmtPct, fmtUSD } from '../lib/format';

const TRIGGER = 0;

// Fixed discount fans always get when subscribing through a creator code.
// Intentionally decoupled from the creator's commission rate (which varies
// per creator and is never surfaced as the fan discount).
const FAN_DISCOUNT_PCT = 10;

export function OverviewScreen() {
  const creator = useAuthStore((s) => s.creator);
  const [sideCodeOpen, setSideCodeOpen] = useState(false);
  if (!creator) return null;

  const codes = useQuery({ queryKey: ['creator', 'codes'], queryFn: creatorApi.listCodes });
  const totals = useQuery({ queryKey: ['creator', 'totals'], queryFn: creatorApi.totals });
  const series = useQuery({ queryKey: ['creator', 'series', '12M'], queryFn: () => creatorApi.earningsSeries('12M') });
  const activity = useQuery({ queryKey: ['creator', 'activity', 5], queryFn: () => creatorApi.activity(5) });

  const t = totals.data;
  const primary = codes.data?.find((c) => c.kind === 'primary' && c.approval === 'approved');
  const sideCount = codes.data?.filter((c) => c.kind === 'side').length ?? 0;
  const atSideLimit = sideCount >= creator.sideLimit;

  return (
    <div style={{ padding: '24px 28px 60px', display: 'grid', gap: 20, maxWidth: 1280 }}>
      {/* Hero */}
      <div
        style={{
          position: 'relative',
          borderRadius: 'var(--r-xl)',
          overflow: 'hidden',
          border: '1px solid var(--line)',
          background: 'var(--bg-elev)',
          padding: '28px 28px',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(280px, 1fr)',
          gap: 28,
        }}
        className="snapmon-hero"
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(80% 120% at 100% 0%, var(--brand-soft), transparent 60%)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Pill tone="brand" size="md" icon={<span style={{ fontSize: 12 }}>◆</span>}>
              Earn on every renewal · for life
            </Pill>
          </div>
          <div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-3)', fontWeight: 600 }}>
              Your earnings this month
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(48px, 6vw, 76px)', fontWeight: 600, letterSpacing: '-0.04em', lineHeight: 1.02, marginTop: 6, color: 'var(--ink)' }}>
              <CountUp value={t?.thisMonthCents ?? 0} format={fmtUSD} decimals={2} duration={1400} trigger={TRIGGER} />
            </div>
            <div style={{ marginTop: 8, color: 'var(--ink-2)', fontSize: 14, maxWidth: 540 }}>
              From <span className="num" style={{ color: 'var(--ink)' }}>{fmtNum(t?.activeSubs ?? 0)}</span> active subscribers using your codes. You earn on every renewal — paid out automatically each month.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, maxWidth: 480 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', border: '1px solid var(--line)', background: 'var(--bg-elev)', borderRadius: 'var(--r-md)' }}>
              <div style={{ width: 38, height: 38, borderRadius: 'var(--r-sm)', background: 'var(--brand-soft)', color: 'var(--brand-ink)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <Icon.Money />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                  Lifetime earnings
                </div>
                <div className="num" style={{ fontWeight: 600, fontSize: 14, marginTop: 2 }}>
                  {fmtUSD(t?.lifetimeEarningsCents ?? 0)}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', border: '1px solid var(--line)', background: 'var(--bg-elev)', borderRadius: 'var(--r-md)' }}>
              <div style={{ width: 38, height: 38, borderRadius: 'var(--r-sm)', background: 'var(--brand-soft)', color: 'var(--brand-ink)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <Icon.Calendar />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                  Next payout · automatic
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, marginTop: 2 }}>
                  <span className="num">{fmtUSD(t?.pendingPayoutCents ?? 0)}</span>
                  <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}> on {t?.nextPayoutDate ? fmtDate(t.nextPayoutDate) : '—'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Share box */}
        <div style={{ position: 'relative', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 'var(--r-lg)', padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Your primary code</div>
            {primary && <Pill tone="good" size="sm">Approved</Pill>}
          </div>
          {primary ? (
            <>
              <CopyField value={primary.code} big />
              <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                Fans get <strong style={{ color: 'var(--ink)' }}>{FAN_DISCOUNT_PCT}% off forever</strong> when subscribing through your code or link.
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Your primary code will appear here once admin assigns one.</div>
          )}
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
        <KpiCard
          label="Your earnings · this month"
          value={t?.thisMonthCents ?? 0}
          format={fmtUSD}
          decimals={2}
          delta={t && t.lastMonthCents > 0 ? ((t.thisMonthCents - t.lastMonthCents) / t.lastMonthCents) * 100 : null}
          sublabel={`vs. ${fmtUSD(t?.lastMonthCents ?? 0)} last month`}
          accent
          trigger={TRIGGER}
        />
        <KpiCard
          label="Pending payout"
          value={t?.pendingPayoutCents ?? 0}
          format={fmtUSD}
          decimals={2}
          sublabel={t?.nextPayoutDate ? `Auto-pays ${fmtDate(t.nextPayoutDate)}` : '—'}
          trigger={TRIGGER}
        />
        <KpiCard
          label="Active subscribers"
          value={t?.activeSubs ?? 0}
          sublabel="active on your codes"
          trigger={TRIGGER}
        />
        <KpiCard
          label="Code uses (all-time)"
          value={t?.totalCodeUses ?? 0}
          sublabel={t ? `${fmtPct(t.conversionRate)} convert to paid` : ''}
          trigger={TRIGGER}
        />
      </div>

      {/* Chart + activity */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)', gap: 14 }} className="snapmon-chart-row">
        <Card padding={20} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Your monthly earnings</div>
              <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>
                Last 12 months · your earnings
              </div>
            </div>
          </div>
          {series.data && series.data.length > 0 ? (
            <EarningsChart data={series.data} height={260} />
          ) : (
            <div style={{ height: 260, display: 'grid', placeItems: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
              {series.isLoading ? 'Loading…' : 'No earnings yet'}
            </div>
          )}
        </Card>

        <Card padding={0} style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '18px 20px 8px', borderBottom: '1px solid var(--line)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Live activity</div>
            <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>Your cut from recent sub events</div>
          </div>
          {(activity.data ?? []).length === 0 ? (
            <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 12 }}>
              {activity.isLoading ? 'Loading…' : 'No activity yet.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {(activity.data ?? []).map((r, i) => (
                <div
                  key={r.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 20px',
                    borderBottom: i === (activity.data ?? []).length - 1 ? 'none' : '1px solid var(--line)',
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'var(--good)',
                      flexShrink: 0,
                      boxShadow: '0 0 0 3px color-mix(in oklab, var(--good) 20%, transparent)',
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      <span className="mono">{r.trainerId ?? '—'}</span>{' '}
                      <span style={{ color: 'var(--ink-3)' }}>· {r.plan ?? '—'}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', display: 'flex', gap: 8, marginTop: 2 }}>
                      <span className="mono">{r.code ?? '—'}</span>
                      <span>·</span>
                      <span>{new Date(r.occurredAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                    </div>
                  </div>
                  <div className="num" style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)' }}>
                    +{fmtUSD(r.amountCents ?? 0)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Codes table */}
      <Card padding={0}>
        <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Your codes</div>
            <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 4 }}>
              All values are your earnings. New side codes require team approval.
            </div>
          </div>
          <button
            disabled={atSideLimit}
            onClick={() => setSideCodeOpen(true)}
            title={atSideLimit ? `You can have up to ${creator.sideLimit} side codes.` : undefined}
            style={{ ...btnGhost, opacity: atSideLimit ? 0.55 : 1, cursor: atSideLimit ? 'not-allowed' : 'pointer' }}
          >
            <Icon.Plus />
            {atSideLimit
              ? `Side code limit reached (${sideCount}/${creator.sideLimit})`
              : 'Request side code'}
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Total uses</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Active subs</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Your MRR</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Your lifetime</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {(codes.data ?? []).map((c) => {
                const pending = c.approval === 'pending';
                const dim = pending ? 0.55 : 1;
                return (
                  <tr key={c.id} style={{ borderTop: '1px solid var(--line)' }}>
                    <td style={tdStyle}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontWeight: 600,
                            fontSize: 13,
                            background: pending ? 'var(--bg-sunken)' : 'var(--brand-soft)',
                            color: pending ? 'var(--ink-3)' : 'var(--brand-ink)',
                            padding: '3px 8px',
                            borderRadius: 'var(--r-sm)',
                            opacity: dim,
                          }}
                        >
                          {c.code}
                        </span>
                        <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>{c.label}</span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {c.kind === 'primary' ? <Pill tone="brand" size="sm">◆ Primary</Pill> : <Pill tone="neutral" size="sm">Side</Pill>}
                    </td>
                    <td style={tdStyle}>
                      {c.approval === 'approved' ? (
                        <Pill tone="good" size="sm">Approved</Pill>
                      ) : c.approval === 'pending' ? (
                        <Pill tone="warn" size="sm">Pending review</Pill>
                      ) : (
                        <Pill tone="bad" size="sm">Rejected</Pill>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', opacity: dim }} className="num">{pending ? '—' : fmtNum(c.uses ?? 0)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', opacity: dim }} className="num">{pending ? '—' : fmtNum(c.activeSubs ?? 0)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', opacity: dim }} className="num">{pending ? '—' : fmtUSD(c.mrrCents ?? 0)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, opacity: dim }} className="num">{pending ? '—' : fmtUSD(c.lifetimeCents ?? 0)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {pending ? (
                        <span style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                          submitted {fmtDate(c.submittedAt)}
                        </span>
                      ) : (
                        <button
                          style={btnIcon}
                          title="Copy code"
                          onClick={() => navigator.clipboard.writeText(c.code).catch(() => undefined)}
                        >
                          <Icon.Copy />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {(codes.data ?? []).length === 0 && !codes.isLoading && (
                <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--ink-3)', padding: '40px 0' }}>
                  No codes yet — wait for admin to assign your primary code.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <RequestSideCodeModal open={sideCodeOpen} onClose={() => setSideCodeOpen(false)} />
    </div>
  );
}
